// app/javascript/widget/adapters.ts — адаптеры движков магазина (doc/shop_adapters.md).
//
// Зачем отдельные файлы. У движка магазина (Moguta.CMS, Bitrix, InSales…)
// корзина и форма заказа устроены одинаково у всех его клиентов, но у каждого
// движка — по-своему. Держать все движки в общем бандле значило бы, что
// салон красоты качает код корзины Moguta, а бандл растёт с каждым новым
// магазином. Поэтому адаптер — свой файл public/widget-adapters/<name>.js,
// который ядро догружает ОДИН раз и только на сайте, где владелец выбрал
// этот движок в кабинете (Assistants::ShopSettings::ADAPTER).
//
// Ядро знает про адаптер ровно этот контракт и ничего сверх него.

/** Что положить: идентификатор в магазине и количество. */
export interface CartItemRequest {
  id: string;
  quantity: number;
  title?: string;
  url?: string;
}

/** Чем заполнить форму заказа: только то, что гость назвал сам. */
export interface CheckoutData {
  name?: string;
  phone?: string;
  email?: string;
  comment?: string;
}

/** Контракт адаптера. Кнопку «Оформить заказ» адаптер НЕ нажимает никогда:
 *  первая же ошибка модели в составе стала бы заказом клиента. */
export interface ShopAdapter {
  /** Путь страницы оформления, если владелец не задал свой. */
  checkoutPath?: string;
  /** Движок ВЫСТАВЛЯЕТ количество позиции (как Moguta: два запроса «×2» дают
   *  2, а не 4) — ядро шлёт абсолютное число. Нет/false — движок ПРИБАВЛЯЕТ,
   *  и ядро шлёт только прирост относительно уже положенного (cart.ts). */
  setsQuantity?: boolean;
  /** Положить позицию в корзину сайта (сессией самого посетителя). */
  addToCart(item: CartItemRequest): Promise<boolean>;
  /** Это страница оформления заказа? */
  isCheckout(location: { pathname: string }): boolean;
  /** Заполнить ПУСТЫЕ поля формы заказа; вернуть число заполненных. */
  fillCheckout(data: CheckoutData, doc: Document): number;
}

/** Реестр на window, куда файл адаптера кладёт себя при загрузке. */
export const REGISTRY_KEY = '__assiistentShopAdapters';
/** Папка адаптеров на платформе (статика public/, см. package.json build:adapters). */
export const ADAPTERS_PATH = '/widget-adapters';
/** Имя адаптера — только такое: оно уезжает в адрес скрипта на чужом сайте,
 *  и что-либо кроме короткого слова в него попасть не должно. */
const NAME_RE = /^[a-z0-9-]{1,32}$/;

/** Штамп сборки (esbuild --define в build:widget): файл адаптера без хеша в
 *  имени, и без штампа браузер держал бы старую версию после деплоя. В
 *  тестах и watch-сборке не задан — адрес без параметра. */
declare const __ADAPTERS_VERSION__: string | undefined;

/** Адрес файла адаптера; null — имени нет или оно не по форме. */
export function adapterUrl(origin: string, name: string | null | undefined): string | null {
  if (!name || !NAME_RE.test(name)) return null;
  const stamp = typeof __ADAPTERS_VERSION__ === 'string' && __ADAPTERS_VERSION__ ? __ADAPTERS_VERSION__ : '';
  const version = stamp ? `?v=${encodeURIComponent(stamp)}` : '';
  return `${origin}${ADAPTERS_PATH}/${name}.js${version}`;
}

/** Похоже на адаптер: все три метода контракта на месте. Проверяем форму, а
 *  не доверяем имени — файл на CDN мог оказаться старым или чужим. */
export function isShopAdapter(value: unknown): value is ShopAdapter {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.addToCart === 'function' &&
    typeof candidate.isCheckout === 'function' &&
    typeof candidate.fillCheckout === 'function'
  );
}

/** Адаптер из реестра окна (файл уже загружен) или null. */
export function registeredAdapter(win: Record<string, unknown>, name: string): ShopAdapter | null {
  const registry = win[REGISTRY_KEY];
  if (typeof registry !== 'object' || registry === null) return null;
  const adapter = (registry as Record<string, unknown>)[name];
  return isShopAdapter(adapter) ? adapter : null;
}

// Одна загрузка на имя за жизнь страницы: адаптер зовут и карточки, и корзина,
// и форма заказа — второй <script> ни к чему.
const loads = new Map<string, Promise<ShopAdapter | null>>();

/** Догрузить адаптер по имени. Никогда не бросает: нет файла, нет сети,
 *  файл не зарегистрировал себя — null, и виджет живёт без корзины. */
export function loadShopAdapter(
  name: string | null | undefined,
  origin: string,
  doc: Document = document,
): Promise<ShopAdapter | null> {
  const url = adapterUrl(origin, name);
  if (!url || !name) return Promise.resolve(null);
  const cached = loads.get(name);
  if (cached) return cached;

  const win = window as unknown as Record<string, unknown>;
  const promise = new Promise<ShopAdapter | null>((resolve) => {
    const existing = registeredAdapter(win, name);
    if (existing) {
      resolve(existing);
      return;
    }
    const script = doc.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => resolve(registeredAdapter(win, name));
    script.onerror = () => resolve(null);
    (doc.head ?? doc.documentElement).appendChild(script);
  });
  loads.set(name, promise);
  return promise;
}

/** Только для тестов: забыть загруженное. */
export function resetAdapterLoads(): void {
  loads.clear();
}
